import { ButtonFlavor, ButtonSize, ButtonType, ExButton } from '@boomi/exosphere';
import { type Dispatch, useEffect, useState } from 'react';
import { useApp } from '../common/AppProvider';
import useAuthentication from '../hooks/useAuthentication';
import { translations } from '../translations/translations';
import type {
    Action,
    AuthenticationStatusResponse,
    OptionValueType,
    Output,
    Trigger,
} from '../types/types';
import {
    checkValidationRules,
    getValidationMessage,
    isTaskItemAuthenticated,
    stringReplace,
} from '../utils/utils';
import ConnectorPicker from './ConnectorPicker';
import SelectedConnector from './SelectedConnector';
import type { SelectedTaskItem } from './TaskEditor';
import TriggerOrActionConfiguration from './TriggerOrActionConfiguration';
import TriggerOrActionPicker from './TriggerOrActionPicker';
import type { TaskEditAction } from './taskReducer';

import './trigger-or-action-drawer-manager.css';

type EditorView = 'connectorPicker' | 'itemConfig';

const TriggerOrActionDrawerManager = ({
    selectedTaskItem,
    dispatchTask,
    authenticationStatus,
    onAuthenticated,
    onClose,
    outputs,
    isLoadingAuthenticationStatus,
}: {
    selectedTaskItem: SelectedTaskItem;
    dispatchTask: Dispatch<TaskEditAction>;
    authenticationStatus: AuthenticationStatusResponse;
    onAuthenticated: () => void;
    onClose: () => void;
    outputs: Output[];
    isLoadingAuthenticationStatus: boolean;
}) => {
    const { language, isOnline } = useApp();
    const { itemType, itemData, itemIndex } = selectedTaskItem;

    const [editorView, setEditorView] = useState<EditorView>(
        itemData ? 'itemConfig' : 'connectorPicker',
    );
    const [currentItemData, setCurrentItemData] = useState<Trigger | Action | null>(itemData);
    const [showValidation, setShowValidation] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    const { onAuthenticate } = useAuthentication(currentItemData?.providerId);

    // Triggers should not offer outputs for their configuration options
    const currentOutputs = itemType === 'trigger' ? [] : outputs;

    const isConnectorAuthenticated = currentItemData
        ? isTaskItemAuthenticated(currentItemData, authenticationStatus)
        : false;

    // Check if the new/updated configuration is valid.
    const isConfigValid = () => {
        // Clear any potentially pre-existing validation results since the user might have
        // corrected them and now wants to re-submit/re-validate the configuration.
        showValidation && setShowValidation(false);
        validationMessage && setValidationMessage(null);

        if (currentItemData) {
            const { validations, values: configurations, options } = currentItemData;

            // Check if any required options are missing/phone numbers are valid.
            const basicValidationFailed = options.some((option) =>
                getValidationMessage(option, configurations[option.id] ?? null, language),
            );

            // Check if any option fails additional validation based on provided JSON logic rules.
            const validationRulesMessage = checkValidationRules(validations, configurations);

            if (basicValidationFailed) {
                setShowValidation(true);
                return false;
            }

            if (validationRulesMessage) {
                setShowValidation(true);
                setValidationMessage(validationRulesMessage);
                return false;
            }
            // NOTE: ^^^ These validation rules come out-of-the-box with the Trigger/Action from
            // the API. The UI then just needs to run them to check for validity.
        }

        return true;
    };

    useEffect(() => {
        setCurrentItemData(selectedTaskItem.itemData ?? null);
    }, [selectedTaskItem]);

    const handleConnectorChange = (connectorId: string) => {
        // Picking a connector creates a blank trigger/action itemData that has only the providerId in it.
        setCurrentItemData({ providerId: connectorId } as Trigger | Action);
        setEditorView('itemConfig');
    };

    const handleConnectorReset = () => {
        // Reset current and pick another connector. Keep current connector, so we can pre-select
        // it in the picker, and lose any other data.
        setCurrentItemData({ providerId: currentItemData!.providerId } as Trigger | Action);
        setShowValidation(false);
        setValidationMessage(null);
        setEditorView('connectorPicker');
    };

    const handleConnectorAuthentication = (e: Event) => {
        e.stopPropagation();
        itemType === 'trigger'
            ? onAuthenticate(currentItemData!.id, null, onAuthenticated)
            : onAuthenticate(null, currentItemData!.id, onAuthenticated);
    };

    const handleTriggerOrActionChange = (selectedTriggerOrAction: Trigger | Action) => {
        setCurrentItemData(selectedTriggerOrAction);
    };

    // Update Trigger/Action (currentItemData) config in local state.
    const handleItemDataConfigChange = (id: string, value: OptionValueType) => {
        if (currentItemData) {
            const updatedItemData = {
                ...currentItemData,
                values: {
                    ...currentItemData.values,
                    ...{ [id]: value },
                },
            };

            setCurrentItemData(updatedItemData);
        }
    };

    // Save Trigger/Action (currentItemData) config from local state into the parent task.
    const handleItemDataConfigSave = () => {
        // NOTE:
        // OG updateAction/updateTrigger updates a single config prop not the whole action/trigger!
        // This won't work in the drawer setup since OG validation was done when we try to save the
        // whole Task, and by then the editing form is closed and the validation is a bit too late.

        if (!isConfigValid()) {
            return;
        }

        if (currentItemData && itemType === 'trigger') {
            // Since there is only one Trigger on a Task, we can use 'addTrigger' for adding or
            // editing as both should replace any existing Trigger in the Task.
            dispatchTask({
                type: 'addTrigger',
                payload: { trigger: currentItemData as Trigger },
            });
        }

        if (currentItemData && itemType === 'action' && itemIndex != null) {
            // Are we editing an existing Action or creating a brand new one?
            // If the initial itemData was not null then we are editing.
            dispatchTask({
                type: itemData ? 'editAction' : 'addAction',
                payload: { index: itemIndex, action: currentItemData },
            });
        }

        onClose(); // close the drawer
    };

    return editorView === 'itemConfig' && currentItemData ? (
        <>
            <div className="drawer-content">
                {/* connector info */}
                <section className="config-section connector">
                    <SelectedConnector
                        itemData={currentItemData}
                        isConnectorAuthenticated={isConnectorAuthenticated}
                        onReset={handleConnectorReset}
                        isLoadingAuthenticationStatus={isLoadingAuthenticationStatus}
                    />
                </section>

                {/* Trigger/action picker */}
                <section className="config-section toa-selection">
                    <h3 className="title lg">{itemType}</h3>
                    <div className="toa-wrapper">
                        <TriggerOrActionPicker
                            itemType={itemType}
                            itemData={currentItemData}
                            onChange={handleTriggerOrActionChange}
                        />
                        {/* Authentication needs the Trigger/Action id to work, so the button
                        follows after the TriggerOrActionPicker. */}
                        <ExButton
                            data-testid={`${currentItemData.providerId}Authenticate`}
                            className={`auth-button exo-button ${isConnectorAuthenticated ? 'is-authenticated' : ''}`}
                            flavor={ButtonFlavor.BASE}
                            size={ButtonSize.LARGE}
                            type={ButtonType.SECONDARY}
                            disabled={!currentItemData.id || !isOnline}
                            onClick={handleConnectorAuthentication}
                        >
                            {isConnectorAuthenticated
                                ? translations[language].TASK_reauthenticate
                                : translations[language].TASK_authenticate}
                        </ExButton>
                    </div>
                    {isConnectorAuthenticated && (
                        <span className="text xxs auth-button-hint">
                            {stringReplace(
                                translations[language].TASK_reauthenticate_hint,
                                itemType,
                            )}
                        </span>
                    )}
                    {currentItemData.id && !isConnectorAuthenticated && (
                        <span className="text xxs auth-button-hint not-authenticated">
                            {stringReplace(translations[language].TASK_authenticate_hint, itemType)}
                        </span>
                    )}
                </section>

                {/* Trigger/action configuration */}
                <section className="config-section toa-configuration">
                    <h3 className="title lg">{translations[language].TASK_configuration}</h3>
                    {showValidation && validationMessage && (
                        <p className="validation-rules-message text xs">{validationMessage}</p>
                    )}
                    <TriggerOrActionConfiguration
                        itemData={currentItemData}
                        onChange={handleItemDataConfigChange}
                        showValidation={showValidation}
                        outputs={currentOutputs}
                    />
                </section>
            </div>
            <footer className="drawer-footer">
                <ExButton
                    data-testid="itemEditorCancel"
                    className="exo-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.SECONDARY}
                    onClick={onClose}
                >
                    {translations[language].COMMON_cancel}
                </ExButton>
                <ExButton
                    data-testid="itemEditorConfirm"
                    className="exo-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.PRIMARY}
                    onClick={handleItemDataConfigSave}
                    disabled={!currentItemData.id}
                >
                    {translations[language].COMMON_confirm}
                </ExButton>
            </footer>
        </>
    ) : (
        <ConnectorPicker
            key={`${itemType}ConnectorPicker`}
            itemType={itemType}
            itemData={currentItemData}
            authenticationStatus={authenticationStatus}
            onConnectorChange={handleConnectorChange}
            onCancel={onClose}
        />
    );
};

export default TriggerOrActionDrawerManager;
